// web/src/features/auth/SignIn.jsx
import React, { useState, useRef, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'

import { auth, db, serverTimestamp } from '@/firebase'
import { signInWithCustomToken, OAuthProvider, signInWithPopup, linkWithCredential, signOut as firebaseSignOut } from 'firebase/auth'
import { MICROSOFT_TENANT_ID, sessionForProfile, canLinkMicrosoft, microsoftError } from './microsoftSession.mjs'
import { getFunctions, httpsCallable } from 'firebase/functions'
import { doc, getDoc, setDoc } from 'firebase/firestore'

import ConfirmDialog from '@/components/ConfirmDialog.jsx'

const lc = s => (s || '').toString().trim().toLowerCase()

const fns = getFunctions(undefined, 'us-central1')
const loginWithFirestore = httpsCallable(fns, 'loginWithFirestore')
const logForgotPasswordClick = httpsCallable(fns, 'logForgotPasswordClick')
const requestPasswordReset = httpsCallable(fns, 'requestPasswordReset')

async function ensureMirrorUserDocByEmail(email) {
  const emailLower = lc(email)
  const ref = doc(db, 'users', emailLower)
  const snap = await getDoc(ref)
  if (!snap.exists()) {
    await setDoc(
      ref,
      {
        email: emailLower,
        emailLower,
        adminLevel: 'none',
        roles: ['user'],
        communities: [],
        isActive: true,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      },
      { merge: true }
    )
  } else {
    await setDoc(
      ref,
      {
        email: emailLower,
        emailLower,
        updatedAt: serverTimestamp()
      },
      { merge: true }
    )
  }
}

export default function SignIn() {
  const nav = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [loginMethod,setLoginMethod]=useState('')
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const pendingMicrosoft=useRef(null)
  const [microsoftEnabled,setMicrosoftEnabled]=useState(null)
  const [passwordEntry,setPasswordEntry]=useState(false)
  useEffect(()=>{let alive=true;httpsCallable(fns,'getMicrosoftSignInSettings')().then(result=>{if(alive)setMicrosoftEnabled(result.data?.enabled===true)}).catch(()=>{if(alive)setMicrosoftEnabled(false)});return()=>{alive=false}},[])

  const [dlgOpen, setDlgOpen] = useState(false)
  const [resetBusy, setResetBusy] = useState(false)
  const [sentNotice, setSentNotice] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    setErr('')
    const em = lc(email)
    if(!loginMethod){
      if(!em){setErr('Enter your company email.');return}
      setBusy(true)
      try{const result=await httpsCallable(fns,'getLoginOptions')({email:em});setLoginMethod(result.data.method);setMicrosoftEnabled(result.data.enabled===true)}catch(error){setErr(error.message||'Unable to check sign-in options.')}finally{setBusy(false)}
      return
    }
    if(loginMethod==='microsoft'){if(microsoftEnabled)await handleMicrosoft();else setErr('Microsoft sign-in is unavailable. Contact your IT team.');return}
    if (!em || !password) {
      setErr('Company email and password are required.')
      return
    }

    try {
      setBusy(true)

      const { data } = await loginWithFirestore({ email: em, password })
      const { token, profile } = data || {}
      if (!token) throw new Error('Login failed: no token returned')

      const signedIn=await signInWithCustomToken(auth, token)
      if(pendingMicrosoft.current){
        if(!canLinkMicrosoft(signedIn.user,pendingMicrosoft.current.email))throw Error('Sign in with the existing account for the same Microsoft email address.')
        const snap=await getDoc(doc(db,'users',lc(signedIn.user.email)))
        sessionForProfile(signedIn.user,snap.exists()?snap.data():null)
        await linkWithCredential(signedIn.user,pendingMicrosoft.current.credential)
        pendingMicrosoft.current=null
        await firebaseSignOut(auth)
        localStorage.removeItem('session');window.dispatchEvent(new Event('sessionchange'))
        setPassword('');setLoginMethod('microsoft');setSentNotice('Microsoft is linked. Continue with Microsoft to finish signing in.');return
      }
      await ensureMirrorUserDocByEmail(profile?.email || em)

      const session = {
        email: profile?.email || em,
        displayName: profile?.displayName || '',
        title: profile?.title || '',
        adminLevel: profile?.adminLevel || 'none',
        roles: Array.isArray(profile?.roles) ? profile.roles : [],
        communities: Array.isArray(profile?.communities)
          ? profile.communities
          : [],
        allCommunities: profile?.allCommunities === true,
        requirePasswordChange: !!profile?.requirePasswordChange
      }
      localStorage.setItem('session', JSON.stringify(session))
      window.dispatchEvent(new Event('sessionchange'))

      if (session.requirePasswordChange) nav('/profile', { replace: true })
      else nav('/tasks', { replace: true })
    } catch (e2) {
      if(pendingMicrosoft.current){await firebaseSignOut(auth).catch(()=>{});localStorage.removeItem('session');window.dispatchEvent(new Event('sessionchange'))}
      const message = e2?.message || ''
      if (message.includes('invalid_password')) {
        setErr(microsoftEnabled && !pendingMicrosoft.current
          ? 'We couldn’t verify your MaintainIQ password. You can switch to Microsoft sign-in below, or use Forgot Password to reset your MaintainIQ password.'
          : 'We couldn’t verify your MaintainIQ password. Use Forgot Password to reset it, then try again.')
      } else {
        const messages = {
          account_inactive: 'Your MaintainIQ account is inactive. Contact your administrator.',
          account_not_found: 'No MaintainIQ account was found for this email. Check the address or contact your administrator.',
          password_not_set: 'This account has no MaintainIQ password. Use Microsoft sign-in or contact your administrator.'
        }
        setErr(messages[message] || message || 'Sign-in couldn’t be completed. Please try again or contact your administrator.')
      }
    } finally {
      setBusy(false)
    }
  }

  async function handleMicrosoft(){
    if(busy)return
    setBusy(true);setErr('');setSentNotice('');pendingMicrosoft.current=null
    try{
      const provider=new OAuthProvider('microsoft.com')
      provider.setCustomParameters({tenant:MICROSOFT_TENANT_ID,prompt:'select_account',...(lc(email)?{login_hint:lc(email)}:{})})
      const result=await signInWithPopup(auth,provider)
      await httpsCallable(fns,'recordMicrosoftSignIn')()
      const address=lc(result.user.email)
      if(!address)throw Error('Microsoft did not return an email address. Contact your administrator.')
      const snap=await getDoc(doc(db,'users',address))
      const session=sessionForProfile(result.user,snap.exists()?snap.data():null)
      localStorage.setItem('session',JSON.stringify(session));window.dispatchEvent(new Event('sessionchange'))
      nav(session.requirePasswordChange?'/profile':'/tasks',{replace:true})
    }catch(error){
      await firebaseSignOut(auth).catch(()=>{})
      localStorage.removeItem('session');window.dispatchEvent(new Event('sessionchange'))
      if(error.code==='auth/account-exists-with-different-credential'){
        const credential=OAuthProvider.credentialFromError(error),address=lc(error.customData?.email)
        if(credential&&address){pendingMicrosoft.current={credential,email:address};setEmail(address);setPasswordEntry(true);setLoginMethod('password');setErr('Sign in once with your existing MaintainIQ password below to link this Microsoft account.')}else setErr('Sign in with your existing account or contact your administrator to link Microsoft sign-in.')
      }else setErr(microsoftError(error))
    }finally{setBusy(false)}
  }

  async function confirmForgot() {
    setDlgOpen(false)
    setErr('')
    setSentNotice('')
    const em = lc(email)
    if (!em) {
      setErr('Enter your company email before requesting a password reset.')
      return
    }

    try {
      setResetBusy(true)
      await requestPasswordReset({ email: em })
      setSentNotice(
        'If that account exists and is active, a password reset link was emailed to you.'
      )
    } catch (e) {
      setErr(e?.message || 'Failed to submit reset request.')
    } finally {
      setResetBusy(false)
    }
  }

  function handleForgotPasswordClick() {
    logForgotPasswordClick({ email: lc(email) }).catch((e) => {
      console.warn('Failed to log forgot password click', e)
    })
    setDlgOpen(true)
  }

  return (
    <div
      // Full-viewport centering, ignoring any layout columns
      style={{
        position: 'fixed',
        inset: 0,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '24px 16px',
        boxSizing: 'border-box',
        pointerEvents: 'auto'
      }}
    >
      <div
        className="card"
        style={{
          maxWidth: 360, // keep it narrow
          width: '100%',
          margin: 0
        }}
      >
 <h1 style={{ 
  marginTop: 0, 
  textAlign: 'center', 
  fontSize: 32, 
  fontWeight: 700 
}}>
  MaintainIQ
</h1>

<div 
  className="muted" 
  style={{ 
    marginTop: -8, 
    marginBottom: 18, 
    textAlign: 'center', 
    fontSize: 14,
    color: '#6b7280'
  }}
>
  Smart Workflows for Smarter Assets
</div>

        <h2 style={{ marginTop: 0, marginBottom: 16 }}>Sign In</h2>

        {err && (
          <div
            style={{
              background: '#fdecea',
              border: '1px solid #f5c2c0',
              color: '#b3261e',
              padding: '10px 12px',
              borderRadius: 10,
              marginBottom: 12
            }}
          >
            {err}
          </div>
        )}

        {sentNotice && (
          <div
            style={{
              background: '#e6f4ea',
              border: '1px solid #c7ebd1',
              color: '#1e7e34',
              padding: '10px 12px',
              borderRadius: 10,
              marginBottom: 12
            }}
          >
            {sentNotice}
          </div>
        )}

        {microsoftEnabled===null&&<p className="muted" role="status" style={{fontSize:12}}>Loading sign-in options…</p>}
        {microsoftEnabled===true&&!passwordEntry&&loginMethod!=='password'&&<div>
          <button type="button" className="btn primary" disabled={busy} onClick={handleMicrosoft} style={{width:'100%'}}>{busy?'Signing in…':'Continue with Microsoft'}</button>
          <button type="button" className="btn secondary" disabled={busy} onClick={()=>{setPasswordEntry(true);setErr('');setSentNotice('')}} style={{width:'100%',marginTop:12}}>Use existing MaintainIQ password</button>
        </div>}
        {(microsoftEnabled===false||passwordEntry||loginMethod==='password')&&<form onSubmit={handleSubmit} className="grid1" style={{ gap: 12 }}>
          <div>
            <label>Email</label>
            <input
              type="email"
              value={email}
              onChange={e => {setEmail(e.target.value);setLoginMethod('');setPassword('');pendingMicrosoft.current=null;setErr('');setSentNotice('')}}
              placeholder="email@scullycompany.com"
              autoFocus
              autoComplete="username"
            />
          </div>
          {loginMethod==='password'&&<><div>
            <label>Password</label>
            <input
              type="password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              placeholder="••••••••"
              autoComplete="current-password"
            />
          </div>
          <div
            style={{
              marginTop: 4,
              display: 'flex',
              gap: 8,
              width: '100%'
            }}
          >
            <button
              type="submit"
              disabled={busy}
              className="btn primary"
              style={{ minWidth: 120 }}
            >
              {busy ? 'Signing in…' : 'Sign In'}
            </button>
            <button
              type="button"
              className="btn secondary"
              onClick={handleForgotPasswordClick}
              disabled={resetBusy}
              style={{ minWidth: 120, marginLeft: 'auto' }} // right-align Forgot Password
            >
              {resetBusy ? 'Sending…' : 'Forgot Password'}
            </button>
          </div></>}
          {!loginMethod&&<button type="submit" className="btn primary" disabled={busy} style={{marginTop:12}}>{busy?'Checking…':'Continue'}</button>}
          {loginMethod==='microsoft'&&<div style={{marginTop:12}}><button type="button" className="btn primary" disabled={busy||!microsoftEnabled} onClick={handleMicrosoft} style={{width:'100%'}}>Continue with Microsoft</button><p className="muted" style={{fontSize:12}}>{microsoftEnabled?'Password recovery is managed by Microsoft or your IT team.':'Microsoft sign-in is currently unavailable. Contact your IT team.'}</p></div>}
          {loginMethod==='password'&&microsoftEnabled&&!pendingMicrosoft.current&&<button type="button" onClick={handleMicrosoft} disabled={busy} style={{marginTop:12}}>Switch to Microsoft sign-in</button>}
        </form>}

        <div 
  className="muted" 
  style={{ 
    marginTop: 20, 
    textAlign: 'center', 
    fontSize: 12, 
    opacity: 0.6 
  }}
>
  Internal Use Only
</div>

        <ConfirmDialog
          open={dlgOpen}
          title="Request Password Reset"
          message={
            <>
              We’ll email a secure password reset link to{' '}
              <strong>{lc(email) || 'your account'}</strong>. The link expires
              after 30 minutes.
            </>
          }
          confirmLabel="Send Request"
          cancelLabel="Cancel"
          tone="default"
          onConfirm={confirmForgot}
          onCancel={() => setDlgOpen(false)}
        />
      </div>
    </div>
  )
}
